import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { BRAND } from "@/constants/brand";

const INTERESTS = [
  { value: "general", label: "General Inquiry" },
  { value: "volunteer", label: "Volunteer" },
  { value: "partner", label: "Partner / Sponsor" },
  { value: "support", label: "I'm a father seeking support" },
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    interest: "general",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const interestLabel =
        INTERESTS.find((i) => i.value === form.interest)?.label || form.interest;
      const res = await fetch(BRAND.formspreeEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          interest: interestLabel,
          message: form.message,
          _subject: `DADication contact — ${interestLabel} from ${form.name}`,
        }),
      });
      if (res.ok) {
        toast.success("Message sent!", {
          description: "Thank you for reaching out. We'll be in touch soon.",
        });
        setForm({ name: "", email: "", message: "", interest: "general" });
      } else {
        const data = await res.json().catch(() => ({}));
        const msg =
          data?.errors?.map((er) => er.message).join(", ") ||
          "Please try again in a moment.";
        toast.error("Something went wrong", { description: msg });
      }
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please check your connection and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-white/10 bg-[#0b0d10] px-4 py-3 text-white placeholder-white/30 outline-none focus:border-[#e2562a] focus:ring-1 focus:ring-[#e2562a] transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#0f1216]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <span className="inline-flex items-center gap-3 text-[#e2562a] uppercase tracking-[0.35em] text-xs font-bold mb-6">
              <span className="h-px w-10 bg-[#e2562a]" />
              Contact
            </span>
            <h2 className="font-display uppercase text-5xl md:text-6xl text-white tracking-tight leading-[0.95]">
              Let's build it together
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Whether you're a father looking for support, a mentor ready to give
              back, or a partner who shares our vision, we'd love to hear from
              you.
            </p>
            <a
              data-testid="contact-email-link"
              href={`mailto:${BRAND.email}`}
              className="mt-8 inline-flex items-center gap-3 text-white hover:text-[#e2562a] transition-colors font-medium"
            >
              <span className="h-11 w-11 flex items-center justify-center bg-[#e2562a] text-white">
                <Mail className="h-5 w-5" />
              </span>
              {BRAND.email}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-[#16191e] border border-white/10 p-8 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
                    Name
                  </label>
                  <input
                    data-testid="contact-name-input"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
                    Email
                  </label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    className={inputCls}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
                  I'm reaching out as
                </label>
                <select
                  data-testid="contact-interest-select"
                  value={form.interest}
                  onChange={update("interest")}
                  className={inputCls}
                >
                  {INTERESTS.map((o) => (
                    <option
                      key={o.value}
                      value={o.value}
                      className="bg-[#0b0d10] text-white"
                    >
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-6">
                <label className="block text-xs font-bold uppercase tracking-wider text-white/60 mb-2">
                  Message
                </label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  placeholder="Tell us how you'd like to get involved..."
                  className={`${inputCls} resize-none`}
                />
              </div>

              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#e2562a] hover:bg-[#c4451d] text-white px-8 py-4 font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0 w-full md:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" /> Send Message
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
